/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import ActiveSubscriptions from 'bundles/composable-page-header/components/ActiveSubscriptions';
import LanguageTranslationMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/LanguageTranslationMenuItem';
import MobileBackNavHeader from 'bundles/composable-page-header/components/mobile-menu-items/MobileBackNavHeader';
import MobileButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileButton';
import MobileLogOutButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileLogOutButton';
import { MOBILE_MAIN_IDENTIFIER } from 'bundles/composable-page-header/constants';
import {
  useCloseMobileMenu,
  useMobileToParentPage,
} from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import type { AccountDropdownLink } from 'bundles/composable-page-header/types';
import { previewShouldShowPostPurchaseChanges } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import { track } from 'bundles/page/lib/event-pulse/sdk';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  dropdownLinks: Array<AccountDropdownLink>;
  courseraPlusSubscriptionCycle?: string | null;
  hideLanguage?: boolean;
};

const styles = {
  yourAccount: css`
    padding-top: var(--cds-spacing-200);
    padding-bottom: var(--cds-spacing-50);
  `,
  ActiveSubscriptionsBorder: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    margin: var(--cds-spacing-50) 0;
  `,
};

const AccountSubMenu = ({ dropdownLinks, hideLanguage, courseraPlusSubscriptionCycle }: Props) => {
  const toParentPage = useMobileToParentPage();
  const closeMenu = useCloseMobileMenu();
  const showCPlus = previewShouldShowPostPurchaseChanges();

  const linkItemClick = (link: AccountDropdownLink) => {
    track('click_profile_menu_item', {
      profileItemName: link.name ?? '',
      profileItemLink: link.href,
    });
    closeMenu?.();
  };

  const mobileMenuItems = dropdownLinks.reduce<React.ReactNode[]>((acc, link) => {
    acc.push(
      <MobileButton
        key={link.name}
        href={link.href}
        label={link.label}
        name={link.name}
        onButtonClick={() => linkItemClick(link)}
      />
    );

    if (link.name === 'account-settings' && !hideLanguage) {
      acc.push(<LanguageTranslationMenuItem key="language" />);
    }

    return acc;
  }, []);

  return (
    <div>
      <MobileBackNavHeader
        label={_t('Main Menu')}
        ariaLabel={_t('Go to main navigation slide')}
        trackingData={{ name: 'account_header' }}
        onClick={() => toParentPage?.(MOBILE_MAIN_IDENTIFIER.MAIN_MENU)}
        trackingName="mobile_header_account_button"
      />
      <Typography2 component="h3" variant="subtitleMedium" css={styles.yourAccount}>
        {_t('Your Account')}
      </Typography2>
      {!!courseraPlusSubscriptionCycle && showCPlus && (
        <div css={styles.ActiveSubscriptionsBorder}>
          <ActiveSubscriptions
            title={_t('Your current subscription:')}
            subtitle={
              courseraPlusSubscriptionCycle === 'MONTHLY' ? _t('Coursera Plus Monthly') : _t('Coursera Plus Annual')
            }
          />
        </div>
      )}
      {[...mobileMenuItems, <MobileLogOutButton tabIndex={0} key="signOut" />]}
    </div>
  );
};

export default AccountSubMenu;
